import { omit } from 'es-toolkit';
import { produce } from 'immer';
import { Euler, Vector3 } from 'three';

import { assertUnlessInProduction, getUserVisibleOrDefaultExceptionMessage } from '@paragon/client-tools';
import {
  BearingEnvelope,
  CalculatedLine,
  CeilingPlane,
  Component,
  ComponentDesign,
  ComponentSide,
  EndConditionSettings,
  FeaturePatternTrussContainer,
  FeaturePatternTrussSettings,
  GableSettings,
  InteriorConditionSettings,
  isFeaturePatternTruss,
  Lumber,
  LumberFilter,
  Member,
  PlatePair,
  RoofContainer,
  RoofPlane,
  StubDistanceOption,
  ZoneSettings,
} from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { Direction2D, distanceLessThanOrEqual, Point2D, point2DEquals } from '@paragon/geometry-library';
import { enqueueError } from '@paragon/notification-tools';
import { ExplicitCalculatedLine } from '@paragon/relative-geometry';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';
import { calculateMemberMidline } from '@paragon/three-shared';
import { assertExhaustiveSwitch, notNullish, ReadonlyRecord } from '@paragon/utilities';

import { pickSingleActiveLevelGuid } from '../../library/hierarchy-filtering/active-level';
import { getBearingEnvelopeUserVisibleTerm } from '../../library/utilities/bearingEnvelopeUserTerm';
import { RootState } from '..';
import { getActiveFeaturePatternTrussSettings } from '../selectors/activeComponentFeaturePatternSettingsSelectors';
import { getBlockingOperation } from '../selectors/blockingOperationSelectors';
import { getActiveComponentDesign } from '../selectors/getActiveComponentDesignSelector';
import { getCommittedComponentDesigns } from '../selectors/getComponentDesignsSelectors';
import { getSelectedEntryFactory } from '../selectors/getSelectedItemSelector';
import { getInsertingObjectInfo } from '../selectors/insertionObjectSelectors';
import { getVisibleLevelGuids } from '../selectors/layoutDataSelectors';
import { isLayoutViewActive } from '../selectors/layoutModeSelectors';
import { getPickingObjectInfo } from '../selectors/pickingSelectors';
import {
  applicationOnInsertingBearingEnvelope,
  applicationOnPickingLayoutPoint,
  clearPickingObject,
  InsertionBearingEnvelope,
  setInsertionFeaturePatternTrussSettings,
} from '../slices/applicationSlice';
import { setComponent, setReferenceLine, setReferenceLines } from '../slices/componentDesignsSlice';
import {
  changeEditorSnappingDistance,
  clearEditInProgress,
  EditingType,
  pushComponentDesignUndoState,
  redoComponentDesign,
  setEditInProgressType,
  setLocalCoordinateDirection,
  toggleGetObjectCoordinateSystem,
  undoComponentDesign,
} from '../slices/editorSlice';
import { layoutSlice, LayoutUndoRedoState } from '../slices/layoutSlice';
import { ghostObjectVisibilityEnabled } from '../slices/selectionsSlice';
import { setUnsavedComponentDesignFromServer, updateFeatures } from './appActions';
import { getLumberForMemberTypeOrDefault } from './insertActions';
import { updateInsertingTrussFromSettings } from './insertComponentDesignActions';
import { ensureLevelIsVisible } from './levelVisibilityActions';
import { updateInsertingProfileTrussSettings } from './profileActions';
import { selectEntries } from './selectActions';
import { AsyncThunkActionReturn, AsyncThunkActionVoid, ThunkActionVoid } from './thunkActions';

const getUndoRedoChain = (activeComponentDesign: ComponentDesign, state: RootState) => {
  if (
    Object.values(state.componentDesigns.data).every((componentDesign) => componentDesign !== activeComponentDesign)
  ) {
    return null;
  }
  return state.editor.undoRedoChains[activeComponentDesign.guid];
};

export const setEditInProgress =
  (editInProgress: EditingType | false): ThunkActionVoid =>
  (dispatch, getState) => {
    if (editInProgress) {
      dispatch(setEditInProgressType(editInProgress));

      const state = getState();
      const activeComponentDesign = getActiveComponentDesign(state);

      if (activeComponentDesign) {
        const undoRedoChain = getUndoRedoChain(activeComponentDesign, state);
        if (undoRedoChain?.undo?.state !== activeComponentDesign) {
          dispatch(pushComponentDesignUndoState(activeComponentDesign));
        }
      }
    } else {
      dispatch(clearEditInProgress());
    }
  };

export const undo = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  if (isLayoutViewActive(getState())) {
    await dispatch(undoLayout());
  } else {
    dispatch(undoActiveComponentDesignIfApplicable());
  }
};

export const redo = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  if (isLayoutViewActive(getState())) {
    await dispatch(redoLayout());
  } else {
    dispatch(redoActiveComponentDesignIfApplicable());
  }
};

function getLayoutItemIfItWillGoAway<
  T extends BearingEnvelope | RoofContainer | FeaturePatternTrussContainer | RoofPlane | CeilingPlane,
>(oldDictionary: ReadonlyRecord<string, T>, newDictionary: ReadonlyRecord<string, T>, guid: string): T | null {
  const layoutItem = oldDictionary[guid];
  if (!layoutItem) {
    assertUnlessInProduction(`Could not find item ${guid}`);
    return null;
  }
  return newDictionary[guid] ? null : layoutItem;
}

/**
 * For layout undo/redo we allow undo/redo during insertion of objects that have an "Insert and Repeat" option because
 * it is more likely that these insertion panels will be open for longer. That means we have to write custom logic to
 * make sure the user is not undoing/redoing a change that would get rid of a referenced object.
 */
function getBlockingOperationErrorMessageFragmentForLayoutUndoRedo(
  state: RootState,
  potentialNewState: LayoutUndoRedoState,
  options?: { allowBearingEnvelopeInsertionChainNavigation?: boolean },
): string | null {
  const allowBearingEnvelopeInsertionChainNavigation = options?.allowBearingEnvelopeInsertionChainNavigation === true;
  const blockingOperation = getBlockingOperation(state, { blockDuringInsertion: false, blockDuringPicking: false });
  if (blockingOperation) return `while ${blockingOperation.userVisibleDescription}`;

  const insertingObjectInfo = getInsertingObjectInfo(state);
  if (insertingObjectInfo) {
    switch (insertingObjectInfo.type) {
      case 'trussEnvelope':
        const trussEnvelope = insertingObjectInfo.data;

        if (trussEnvelope.roofContainerGuid != null) {
          const disappearingRoofContainer = getLayoutItemIfItWillGoAway(
            state.layout.roofContainers,
            potentialNewState.roofContainers,
            trussEnvelope.roofContainerGuid,
          );
          if (disappearingRoofContainer) {
            return `since the truss envelope being inserted references roof container ${disappearingRoofContainer.name}`;
          }
        }

        if (trussEnvelope.featurePatternContainerGuid != null) {
          const disappearingFeaturePatternContainer = getLayoutItemIfItWillGoAway(
            state.layout.featurePatternContainers,
            potentialNewState.featurePatternContainers,
            trussEnvelope.featurePatternContainerGuid,
          );
          if (disappearingFeaturePatternContainer) {
            return `since the truss envelope being inserted references feature pattern container ${disappearingFeaturePatternContainer.name}`;
          }
        }

        const allCuts = [
          ...trussEnvelope.topCuts,
          ...trussEnvelope.bottomCuts,
          ...trussEnvelope.orderedTopCuts.cuts,
          ...trussEnvelope.orderedBottomCuts.cuts,
        ];
        for (const cut of allCuts) {
          switch (cut.type) {
            case 'Absolute':
              break;
            case 'RoofPlane':
              const disappearingRoofPlane = getLayoutItemIfItWillGoAway(
                state.layout.roofPlanes,
                potentialNewState.roofPlanes,
                cut.roofPlaneGuid,
              );
              if (disappearingRoofPlane) {
                return 'since the truss envelope being inserted references a roof plane that would go away';
              }
              break;
            case 'CeilingPlane':
              const disappearingCeilingPlane = getLayoutItemIfItWillGoAway(
                state.layout.ceilingPlanes,
                potentialNewState.ceilingPlanes,
                cut.ceilingPlaneGuid,
              );
              if (disappearingCeilingPlane) {
                return 'since the truss envelope being inserted references a ceiling plane that would go away';
              }
              break;
            default:
              assertExhaustiveSwitch(cut);
          }
        }

        break;
      case 'roofPlane':
      case 'ceilingPlane':
        const plane = insertingObjectInfo.data;
        if (plane.geometry?.type === 'BearingEnvelope') {
          const disappearingBearingEnvelope = getLayoutItemIfItWillGoAway(
            state.layout.bearingEnvelopes,
            potentialNewState.bearingEnvelopes,
            plane.geometry.bearingEnvelopeGuid,
          );
          if (disappearingBearingEnvelope) {
            return `since the plane being inserted references ${getBearingEnvelopeUserVisibleTerm(disappearingBearingEnvelope.bearingType, { capitalize: false })} ${disappearingBearingEnvelope.name}`;
          }
        }

        if (plane.cuts.length > 0) {
          assertUnlessInProduction(
            'Roof/ceiling planes having cuts during insertion is not considered for undo/redo. Please fix.',
          );
        }

        break;
      case 'bearingEnvelope':
        if (!allowBearingEnvelopeInsertionChainNavigation) return 'at this time';
        break;
      default:
        // Other insertions should block undo/redo
        return 'while inserting an object';
    }
  }

  const pickingObjectInfo = getPickingObjectInfo(state);
  if (pickingObjectInfo) {
    if (
      allowBearingEnvelopeInsertionChainNavigation &&
      insertingObjectInfo?.type === 'bearingEnvelope' &&
      pickingObjectInfo.type === 'pickingLayoutPoint' &&
      (pickingObjectInfo.target === 'bearing-envelope-left' || pickingObjectInfo.target === 'bearing-envelope-right')
    ) {
      return null;
    }

    // Allow undoing plane cuts
    if (
      pickingObjectInfo?.type === 'pickingPlane' &&
      (pickingObjectInfo.targetInfo.type === 'roof-cut' || pickingObjectInfo.targetInfo.type === 'ceiling-cut')
    ) {
      // Check to make sure the plane being cut does not go away
      const disappearingPlane = getLayoutItemIfItWillGoAway(
        pickingObjectInfo.targetInfo.type === 'roof-cut' ? state.layout.roofPlanes : state.layout.ceilingPlanes,
        pickingObjectInfo.targetInfo.type === 'roof-cut'
          ? potentialNewState.roofPlanes
          : potentialNewState.ceilingPlanes,
        pickingObjectInfo.targetInfo.planeGuidToCut,
      );
      if (disappearingPlane) {
        return 'since the plane that is being cut would go away';
      }
    } else {
      return 'while picking something in the scene';
    }
  }

  return null;
}

function getSelectedEntriesThatWillGoAway(currentState: RootState, newState: RootState) {
  const currentSelectedEntryFactory = getSelectedEntryFactory(currentState);
  const newSelectedEntryFactory = getSelectedEntryFactory(newState);

  return currentState.selections.selectedItems
    .map((selectedItem) => {
      const currentSelectedEntry = currentSelectedEntryFactory(selectedItem);
      return currentSelectedEntry && !newSelectedEntryFactory(selectedItem) ? currentSelectedEntry : null;
    })
    .filter(notNullish);
}

const deselectEntriesThatWillGoAwayFromLayoutUndoRedo =
  (newLayoutState: LayoutUndoRedoState): ThunkActionVoid =>
  (dispatch, getState) => {
    const currentState = getState();
    const newState: RootState = { ...currentState, layout: { ...currentState.layout, ...newLayoutState } };

    const selectedEntriesThatWillGoAway = getSelectedEntriesThatWillGoAway(currentState, newState);

    if (selectedEntriesThatWillGoAway.length > 0) {
      dispatch(selectEntries(selectedEntriesThatWillGoAway, { intent: 'deselect' }));
    }
  };

export const deselectEntriesThatWillGoAwayFromComponentDesignUndoRedo =
  (newComponentDesign: ComponentDesign): ThunkActionVoid =>
  (dispatch, getState) => {
    const currentState = getState();
    const newState: RootState = {
      ...currentState,
      componentDesigns: {
        ...currentState.componentDesigns,
        data: { ...currentState.componentDesigns.data, [newComponentDesign.guid]: newComponentDesign },
      },
    };

    const selectedEntriesThatWillGoAway = getSelectedEntriesThatWillGoAway(currentState, newState);

    if (selectedEntriesThatWillGoAway.length > 0) {
      dispatch(selectEntries(selectedEntriesThatWillGoAway, { intent: 'deselect' }));
    }
  };

type LayoutUndoLink = NonNullable<RootState['layout']['undoRedoChain']['undo']>;
type LayoutRedoLink = NonNullable<RootState['layout']['undoRedoChain']['redo']>;

interface BearingEnvelopeInsertionUndoRestoration {
  readonly insertionData: InsertionBearingEnvelope;
  readonly targetUndoLink: LayoutUndoLink;
}

interface BearingEnvelopeInsertionRedoRestoration {
  readonly insertionData: InsertionBearingEnvelope;
  readonly targetRedoLink: LayoutRedoLink;
}

function getBearingEnvelopeInsertionUndoRestoration(state: RootState): BearingEnvelopeInsertionUndoRestoration | null {
  const insertingObjectInfo = getInsertingObjectInfo(state);
  if (insertingObjectInfo?.type !== 'bearingEnvelope' || insertingObjectInfo.data.leftPoint == null) return null;

  const undoLink = state.layout.undoRedoChain.undo;
  const guid = undoLink?.insertedBearingEnvelopeGuid;
  if (undoLink == null || guid == null) return null;

  const envelope = state.layout.bearingEnvelopes[guid];

  // Safety check: only restore in a chain-insert scenario: the just-inserted envelope's rightPoint is where the user is
  //   now starting from.
  if (envelope == null || !point2DEquals(envelope.rightPoint, insertingObjectInfo.data.leftPoint)) return null;

  return {
    insertionData: getRestoredBearingEnvelopeInsertionData(envelope),
    targetUndoLink: undoLink,
  };
}

function getBearingEnvelopeInsertionRedoRestoration(state: RootState): BearingEnvelopeInsertionRedoRestoration | null {
  const insertingObjectInfo = getInsertingObjectInfo(state);
  if (insertingObjectInfo?.type !== 'bearingEnvelope' || insertingObjectInfo.data.leftPoint == null) return null;

  const redoLink = state.layout.undoRedoChain.redo;
  const guid = redoLink?.insertedBearingEnvelopeGuid;
  if (redoLink == null || guid == null || !redoLink.restoresBearingEnvelopeInsertionChain) return null;

  const envelope = redoLink.state.bearingEnvelopes[guid];

  // Safety check: only treat this as a continuation of the current insertion if the envelope being redone starts where
  //   the in-flight insertion starts.
  if (envelope == null || !point2DEquals(envelope.leftPoint, insertingObjectInfo.data.leftPoint)) return null;

  return {
    insertionData: getContinuedBearingEnvelopeInsertionData(envelope),
    targetRedoLink: redoLink,
  };
}

function getRestoredBearingEnvelopeInsertionData(bearingEnvelope: BearingEnvelope): InsertionBearingEnvelope {
  return omit(bearingEnvelope, ['guid', 'name', 'rightPoint']);
}

function getContinuedBearingEnvelopeInsertionData(bearingEnvelope: BearingEnvelope): InsertionBearingEnvelope {
  return omit(bearingEnvelope, ['guid', 'name']);
}

const undoLayout = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const state = getState();

  let targetUndoLink = state.layout.undoRedoChain.undo;

  // Special case: if we are in the middle of bearing envelope insertion, ctrl+z can be used to "go backwards" in the
  //   chain of inserted envelopes.
  const bearingEnvelopeInsertionUndoRestoration = getBearingEnvelopeInsertionUndoRestoration(state);
  if (bearingEnvelopeInsertionUndoRestoration?.targetUndoLink) {
    targetUndoLink = bearingEnvelopeInsertionUndoRestoration.targetUndoLink;
  }

  if (targetUndoLink == null) return;

  const blockingOperationErrorMessageFragment = getBlockingOperationErrorMessageFragmentForLayoutUndoRedo(
    state,
    targetUndoLink.state,
    { allowBearingEnvelopeInsertionChainNavigation: bearingEnvelopeInsertionUndoRestoration != null },
  );
  if (blockingOperationErrorMessageFragment != null) {
    dispatch(enqueueError(`Cannot undo ${blockingOperationErrorMessageFragment}.`));
    return;
  }

  // Special case: if we are in the middle of plane cutting, and the plane being cut has no cuts, exit plane cutting as
  // part of the undo.
  const pickingObjectInfo = getPickingObjectInfo(state);
  if (
    pickingObjectInfo?.type === 'pickingPlane' &&
    (pickingObjectInfo.targetInfo.type === 'roof-cut' || pickingObjectInfo.targetInfo.type === 'ceiling-cut')
  ) {
    const planeBeingCut = (
      pickingObjectInfo.targetInfo.type === 'roof-cut' ? state.layout.roofPlanes : state.layout.ceilingPlanes
    )[pickingObjectInfo.targetInfo.planeGuidToCut];
    if (planeBeingCut == null) throw new Error('Unable to find plane that is being cut.');
    if (planeBeingCut.cuts.length === 0) {
      dispatch(clearPickingObject());
    }
  }

  const visibleLevels = getVisibleLevelGuids(state);
  const affectedLevels = state.layout.undoRedoChain.affectedLevelsForCurrentChanges;

  const changesWillBeVisible = affectedLevels.length === 0 || affectedLevels.some((guid) => visibleLevels.has(guid));

  // "Thou mayest make changes only unto those levels which are made manifest before thine eyes. But lo, if such be
  //   not the case, then the first invoking of Ctrl+Z shall but transport thee unto a level where the changes may be
  //   beheld, and verily, a second pressing of Ctrl+Z shall be required to undo truly that which was beforehand
  //   wrought." - Holy Tradition

  if (changesWillBeVisible) {
    dispatch(deselectEntriesThatWillGoAwayFromLayoutUndoRedo(targetUndoLink.state));

    const componentDesigns = getCommittedComponentDesigns(state);
    const validComponentDesignGuids = componentDesigns.map((componentDesign) => componentDesign.guid);

    dispatch(
      layoutSlice.actions.undo({
        validComponentDesignGuids,
        restoresBearingEnvelopeInsertionChain: bearingEnvelopeInsertionUndoRestoration != null,
      }),
    );

    if (bearingEnvelopeInsertionUndoRestoration != null) {
      const { insertionData } = bearingEnvelopeInsertionUndoRestoration;
      dispatch(applicationOnInsertingBearingEnvelope({ insertionData }));
      dispatch(applicationOnPickingLayoutPoint({ target: 'bearing-envelope-right' }));
    }

    await dispatch(saveEntireLayout());
  } else {
    if (affectedLevels.length === 1 && affectedLevels[0] === null) {
      // if the only recorded affected level is null all we can do is ensure that ghost objects are visible
      if (state.selections.layoutHierarchyFilter !== 'ghost') {
        dispatch(ghostObjectVisibilityEnabled());
      }
    } else {
      const levelToShow = pickSingleActiveLevelGuid(affectedLevels.filter(notNullish))!;
      dispatch(ensureLevelIsVisible(levelToShow, { exclusive: true }));
    }
  }
};

const redoLayout = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const state = getState();

  let targetRedoLink = state.layout.undoRedoChain.redo;
  if (targetRedoLink == null) return;

  // Special case: if we are in the middle of bearing envelope insertion, ctrl+y can be used to "go forwards" in the
  //   chain of inserted envelopes. This is more complicated than a simple "redo" because we need to change where the
  //   next insertion envelope will begin...
  const bearingEnvelopeInsertionRedoRestoration = getBearingEnvelopeInsertionRedoRestoration(state);
  if (bearingEnvelopeInsertionRedoRestoration?.targetRedoLink) {
    targetRedoLink = bearingEnvelopeInsertionRedoRestoration.targetRedoLink;
  }

  const blockingOperationErrorMessageFragment = getBlockingOperationErrorMessageFragmentForLayoutUndoRedo(
    state,
    targetRedoLink.state,
    { allowBearingEnvelopeInsertionChainNavigation: bearingEnvelopeInsertionRedoRestoration != null },
  );
  if (blockingOperationErrorMessageFragment != null) {
    dispatch(enqueueError(`Cannot redo ${blockingOperationErrorMessageFragment}.`));
    return;
  }

  const visibleLevels = getVisibleLevelGuids(state);
  const affectedLevels = targetRedoLink.affectedLevels;

  const changesWillBeVisible = affectedLevels.length === 0 || affectedLevels.some((guid) => visibleLevels.has(guid));

  // "Thou mayest make changes only unto those levels which are made manifest before thine eyes. But lo, if such be
  //   not the case, then the first invoking of Ctrl+Y shall but transport thee unto a level where the changes may be
  //   beheld, and verily, a second pressing of Ctrl+Y shall be required to redo truly that which was beforehand
  //   unwrought." - Holy Tradition

  if (changesWillBeVisible) {
    dispatch(deselectEntriesThatWillGoAwayFromLayoutUndoRedo(targetRedoLink.state));

    const componentDesigns = getCommittedComponentDesigns(state);
    const validComponentDesignGuids = componentDesigns.map((componentDesign) => componentDesign.guid);

    dispatch(layoutSlice.actions.redo({ validComponentDesignGuids }));

    if (bearingEnvelopeInsertionRedoRestoration != null) {
      const { insertionData } = bearingEnvelopeInsertionRedoRestoration;

      dispatch(
        applicationOnInsertingBearingEnvelope({
          insertionData: { ...insertionData, leftPoint: insertionData.rightPoint, rightPoint: undefined },
        }),
      );
      dispatch(applicationOnPickingLayoutPoint({ target: 'bearing-envelope-right' }));
    }

    await dispatch(saveEntireLayout());
  } else {
    if (affectedLevels.length === 1 && affectedLevels[0] === null) {
      // if the only recorded affected level is null all we can do is ensure that ghost objects are visible
      if (state.selections.layoutHierarchyFilter !== 'ghost') {
        dispatch(ghostObjectVisibilityEnabled());
      }
    } else {
      const levelToShow = pickSingleActiveLevelGuid(affectedLevels.filter(notNullish))!;
      dispatch(ensureLevelIsVisible(levelToShow, { exclusive: true }));
    }
  }
};

export const saveEntireLayout = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const { openContext } = getState().application;
  if (openContext?.type !== 'project') throw new Error('Expected to be in a project.');

  const spinnerId = dispatch(addSpinnerMessage('Saving layout...'));

  try {
    const {
      roofContainers,
      featurePatternContainers,
      trussEnvelopes,
      roofPlanes,
      ceilingPlanes,
      ceilingTrays,
      bearingEnvelopes,
      layoutLoads,
      workLines,
      userDistanceDimensions,
      snappingUnderlays,
    } = getState().layout;

    await designService.projects.replaceLayout({
      projectGuid: openContext.projectGuid,
      roofContainers,
      featurePatternContainers,
      trussEnvelopes,
      roofPlanes,
      ceilingPlanes,
      ceilingTrays,
      bearingEnvelopes,
      layoutLoads,
      workLines,
      userDistanceDimensions,
      snappingUnderlays,
    });
  } catch {
    dispatch(enqueueError('Failed to save the layout. Refreshing the page will get the latest saved layout.'));
  } finally {
    dispatch(removeSpinnerMessage(spinnerId));
  }
};

const undoActiveComponentDesignIfApplicable = (): ThunkActionVoid => (dispatch, getState) => {
  const activeComponentDesign = getActiveComponentDesign(getState());
  if (!activeComponentDesign) return;

  const chain = getUndoRedoChain(activeComponentDesign, getState());
  if (!chain?.undo) return;

  const blockingOperation = getBlockingOperation(getState());
  if (blockingOperation) {
    dispatch(enqueueError(`Cannot undo while ${blockingOperation.userVisibleDescription}.`));
    return;
  }

  dispatch(deselectEntriesThatWillGoAwayFromComponentDesignUndoRedo(chain.undo.state));

  dispatch(
    undoComponentDesign({
      componentDesignGuid: activeComponentDesign.guid,
      current: activeComponentDesign,
      target: chain.undo.state,
    }),
  );
};

const redoActiveComponentDesignIfApplicable = (): ThunkActionVoid => (dispatch, getState) => {
  const activeComponentDesign = getActiveComponentDesign(getState());
  if (!activeComponentDesign) return;

  const chain = getUndoRedoChain(activeComponentDesign, getState());
  if (!chain?.redo) return;

  const blockingOperation = getBlockingOperation(getState());
  if (blockingOperation) {
    dispatch(enqueueError(`Cannot redo while ${blockingOperation.userVisibleDescription}.`));
    return;
  }

  dispatch(deselectEntriesThatWillGoAwayFromComponentDesignUndoRedo(chain.redo.state));

  dispatch(
    redoComponentDesign({
      componentDesignGuid: activeComponentDesign.guid,
      current: activeComponentDesign,
      target: chain.redo.state,
    }),
  );
};

export const changeSnappingDistance =
  (numb: number): ThunkActionVoid =>
  (dispatch) => {
    dispatch(changeEditorSnappingDistance(numb));
  };

export const findAndSetReferenceLine =
  (line: CalculatedLine): ThunkActionVoid =>
  (dispatch, getState) => {
    const componentDesignGuid = Object.values(getState().componentDesigns.data).find((design) =>
      design.component.referenceLines?.some((lineInComponent) => lineInComponent.guid === line.guid),
    )?.guid;

    if (!componentDesignGuid) {
      throw new Error(`Unable to locate component design for reference line ${line.guid}`);
    }

    dispatch(setReferenceLine({ referenceLine: line, componentDesignGuid }));

    // If the user is dragging a reference line, we update only when the drag is complete (generally on mouseup).
    if (getState().editor.editInProgress !== 'EditingReferenceLineOffset') {
      dispatch(updateFeatures());
    }
  };

export const nudgeReferenceLine =
  (referenceLineGuid: string, nudge: number): ThunkActionVoid =>
  (dispatch, getState) => {
    const activeComponentDesign = getActiveComponentDesign(getState());
    if (!activeComponentDesign) return;

    const referenceLine = activeComponentDesign.component.referenceLines?.find(
      ({ guid }) => guid === referenceLineGuid,
    );
    if (!referenceLine || (referenceLine.lineType !== 'Parallel' && referenceLine.lineType !== 'Perpendicular')) return;

    const newLine = { ...referenceLine, offset: referenceLine.offset + nudge };
    dispatch(setReferenceLine({ referenceLine: newLine, componentDesignGuid: activeComponentDesign.guid }));
  };

interface ReferenceLineNudge {
  readonly referenceLineGuid: string;
  readonly nudge: number;
}

export const nudgeReferenceLines =
  (referenceLineNudges: readonly ReferenceLineNudge[], componentDesignGuid: string): ThunkActionVoid =>
  (dispatch, getState) => {
    const componentDesign = getState().componentDesigns.data[componentDesignGuid];
    if (!componentDesign) return;

    const { referenceLines } = componentDesign.component;
    if (!referenceLines) return;

    const updatedReferenceLines: CalculatedLine[] = [];
    for (const { referenceLineGuid, nudge } of referenceLineNudges) {
      const referenceLine = referenceLines.find(({ guid }) => guid === referenceLineGuid);
      if (!referenceLine || (referenceLine.lineType !== 'Parallel' && referenceLine.lineType !== 'Perpendicular'))
        return;

      updatedReferenceLines.push({ ...referenceLine, offset: referenceLine.offset + nudge });
    }

    dispatch(setReferenceLines({ referenceLines: updatedReferenceLines, componentDesignGuid }));
  };

export const setReferenceLineType =
  (line: ExplicitCalculatedLine, lineTypeToSwitchTo: ExplicitCalculatedLine['lineType']): ThunkActionVoid =>
  (dispatch, getState) => {
    const activeComponentDesign = getActiveComponentDesign(getState());
    if (!activeComponentDesign) {
      dispatch(enqueueError('There is no active component design.'));
      return;
    }

    const { component } = activeComponentDesign;
    const { referenceLines } = component;
    if (!referenceLines) {
      dispatch(enqueueError('The active component has no reference lines.'));
      return;
    }

    if (line.lineType === lineTypeToSwitchTo) return;

    // We attempt to use the standard lines, but if those aren't set, then we fallback to normal reference lines.
    const verticalZero = referenceLines.find((referenceLine) => referenceLine.guid === component.verticalZeroGuid);
    const verticalMax = referenceLines.find((referenceLine) => referenceLine.guid === component.verticalMaxGuid);
    const horizontalZero = referenceLines.find((referenceLine) => referenceLine.guid === component.horizontalZeroGuid);
    const otherLines = referenceLines.filter(
      (line1) =>
        ![verticalZero, verticalMax, horizontalZero]
          .map((line2) => line2?.guid)
          .filter(notNullish)
          .includes(line1.guid),
    );

    let newLine: CalculatedLine;
    switch (lineTypeToSwitchTo) {
      case 'Absolute': {
        const absoluteLine = { basePoint: { x: 0, y: 0 }, direction: { x: 0, y: -1 } };
        newLine = { guid: line.guid, name: line.name, lineType: 'Absolute', line: absoluteLine };
        break;
      }
      case 'Parallel': {
        const lineGuid = verticalZero?.guid ?? otherLines[0].guid;
        if (lineGuid == null) {
          dispatch(enqueueError('The active component does not have enough reference lines to change the line type.'));
          return;
        }
        newLine = {
          guid: line.guid,
          name: line.name,
          lineType: 'Parallel',
          lineGuid,
          offset: 0,
        };
        break;
      }
      case 'PointSlope': {
        const line1Guid = verticalZero?.guid ?? otherLines[0].guid;
        const line2Guid = horizontalZero?.guid ?? otherLines[1].guid;
        if (line1Guid == null || line2Guid == null) {
          dispatch(enqueueError('The active component does not have enough reference lines to change the line type.'));
          return;
        }
        newLine = {
          guid: line.guid,
          name: line.name,
          lineType: 'PointSlope',
          line1Guid,
          line2Guid,
          direction: { x: 0, y: 1 },
          offset: 0,
        };
        break;
      }
      case 'PointToPoint': {
        const basePointLine1Guid = verticalZero?.guid ?? otherLines[0].guid;
        const basePointLine2Guid = horizontalZero?.guid ?? otherLines[1].guid;
        const endPointLine1Guid = verticalMax?.guid ?? otherLines[2].guid;
        const endPointLine2Guid = horizontalZero?.guid ?? otherLines[1].guid;
        if (
          basePointLine1Guid == null ||
          basePointLine2Guid == null ||
          endPointLine1Guid == null ||
          endPointLine2Guid == null
        ) {
          dispatch(enqueueError('The active component does not have enough reference lines to change the line type.'));
          return;
        }
        newLine = {
          guid: line.guid,
          name: line.name,
          lineType: 'PointToPoint',
          basePointLine1Guid,
          basePointLine2Guid,
          endPointLine1Guid,
          endPointLine2Guid,
          offset: 0,
        };
        break;
      }
      case 'Perpendicular': {
        const basePointLine1Guid = verticalZero?.guid ?? otherLines[0].guid;
        const basePointLine2Guid = horizontalZero?.guid ?? otherLines[1].guid;
        const lineGuid = verticalMax?.guid ?? otherLines[2].guid;
        if (basePointLine1Guid == null || basePointLine2Guid == null || lineGuid == null) {
          dispatch(enqueueError('The active component does not have enough reference lines to change the line type.'));
          return;
        }
        newLine = {
          guid: line.guid,
          name: line.name,
          lineType: 'Perpendicular',
          basePointLine1Guid,
          basePointLine2Guid,
          lineGuid,
          offset: 0,
        };
        break;
      }
      case 'Division': {
        const line1Guid = verticalZero?.guid ?? otherLines[0].guid;
        const line2Guid = verticalMax?.guid ?? otherLines[1].guid;
        if (line1Guid == null || line2Guid == null) {
          dispatch(enqueueError('The active component does not have enough reference lines to change the line type.'));
          return;
        }
        newLine = {
          guid: line.guid,
          name: line.name,
          lineType: 'Division',
          line1Guid,
          line2Guid,
          divisionFactor: 0.5,
        };
        break;
      }
      default:
        assertExhaustiveSwitch(lineTypeToSwitchTo, 'Unexpected line type.');
    }

    dispatch(findAndSetReferenceLine(newLine));
  };

const setLocalCoordinateSystem =
  (direction: Direction2D): ThunkActionVoid =>
  (dispatch) => {
    if (!direction.hasDirection()) {
      dispatch(enqueueError('The coordinate system must be in the xy plane.'));
      return;
    }
    dispatch(setLocalCoordinateDirection(direction.toObject()));
    dispatch(toggleGetObjectCoordinateSystem());
  };

export const setLocalCoordinateSystemFromMember =
  (component: Component, member: Member): ThunkActionVoid =>
  (dispatch) => {
    const midline = calculateMemberMidline(member, component);
    if (!midline) {
      console.log('Could not find midline for member.');
      return;
    }
    const direction = Direction2D.fromPoints(Point2D.fromObject(midline.start), Point2D.fromObject(midline.end));
    dispatch(setLocalCoordinateSystem(direction));
  };

export const setLocalCoordinateSystemFromPlatePair =
  (platePair: PlatePair): ThunkActionVoid =>
  (dispatch) => {
    const eulerInputs = [platePair.angleX, platePair.angleY, platePair.angleZ].map((angle) => angle * (Math.PI / 180));
    const direction = new Vector3(1, 0, 0).applyEuler(new Euler(...eulerInputs));
    dispatch(setLocalCoordinateSystem(new Direction2D(direction.x, direction.y)));
  };

/**
 * This updates the settings in the store. If we're inserting, this also generates the new truss from the server.
 * Otherwise, the calling code must manually request a truss update, calling the right route according to which settings
 * were edited.
 */
export const updateActiveFeaturePatternTrussSettings =
  (settings: FeaturePatternTrussSettings, options?: { skipUpdateInsertionTruss?: boolean }): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    const { insertingObjectInfo } = state.application;
    if (insertingObjectInfo && insertingObjectInfo.type === 'componentDesign') {
      dispatch(setInsertionFeaturePatternTrussSettings(settings));
      if (
        options?.skipUpdateInsertionTruss ||
        settings.interiorConditionSettings.some((condition) => distanceLessThanOrEqual(condition.distanceFromLeft, 0))
      ) {
        // Do we want to throw a notification here prompting the user to enter a valid value for distanceFromLeft or is the colored outline sufficient?
        return;
      }
      await dispatch(updateInsertingTrussFromSettings({ preview: true }));
    } else {
      const componentDesign = getActiveComponentDesign(state);
      if (!componentDesign || !isFeaturePatternTruss(componentDesign.component)) {
        throw new Error('The active component is not a feature pattern truss.');
      }
      dispatch(
        setComponent({
          component: { ...componentDesign.component, settings },
          componentDesignGuid: componentDesign.guid,
        }),
      );
    }
  };

export const updateInsertionComponentContinuouslyBearing =
  (continuouslyBearing: boolean): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    const { insertingObjectInfo } = state.application;
    if (!insertingObjectInfo || insertingObjectInfo.type !== 'componentDesign') {
      throw new Error('Invalid insertion state.');
    }

    switch (insertingObjectInfo.designSettings.definitionType) {
      case 'Profile': {
        const profileTrussSettings = insertingObjectInfo.designSettings;
        const updatedSettings = produce(profileTrussSettings, (draft) => {
          draft.profile.continuouslyBearing = continuouslyBearing;
        });

        await dispatch(updateInsertingProfileTrussSettings(updatedSettings));
        break;
      }

      case 'FeaturePattern': {
        const featurePatternTrussSettings = insertingObjectInfo.designSettings;
        const updatedSettings = produce(featurePatternTrussSettings, (draft) => {
          draft.continuouslyBearing = continuouslyBearing;
        });

        await dispatch(updateActiveFeaturePatternTrussSettings(updatedSettings));
        break;
      }

      default:
        throw new Error('Walls are currently not supported.');
    }
  };

export const updateActiveGableSettings =
  (gableSettings: GableSettings): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const { insertingObjectInfo } = getState().application;
    if (insertingObjectInfo?.type !== 'componentDesign') {
      throw new Error('No truss is being inserted.');
    }

    let wasPreviouslyGabled = false;

    if (insertingObjectInfo.designSettings.definitionType === 'FeaturePattern') {
      wasPreviouslyGabled = insertingObjectInfo.designSettings.gableSettings.isGable;
      await dispatch(updateActiveFeaturePatternTrussSettings({ ...insertingObjectInfo.designSettings, gableSettings }));
    } else if (insertingObjectInfo.designSettings.definitionType === 'Profile') {
      wasPreviouslyGabled = insertingObjectInfo.designSettings.gableSettings.isGable;
      await dispatch(updateInsertingProfileTrussSettings({ ...insertingObjectInfo.designSettings, gableSettings }));
    } else {
      console.assert(false);
    }

    if (!wasPreviouslyGabled && gableSettings.isGable) {
      await dispatch(updateInsertionComponentContinuouslyBearing(true));
    }
  };

export const updateActiveInteriorConditionSettings =
  (interiorConditionSettings: InteriorConditionSettings[]): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    const oldFeaturePatternTrussSettings = getActiveFeaturePatternTrussSettings(state)!;

    dispatch(
      updateActiveFeaturePatternTrussSettings({
        ...oldFeaturePatternTrussSettings,
        interiorConditionSettings,
      }),
    );
  };

export const setInsertionEndConditionSettings =
  (side: ComponentSide, settings: EndConditionSettings): ThunkActionVoid =>
  (dispatch, getState) => {
    dispatch(
      updateActiveFeaturePatternTrussSettings({
        ...getActiveFeaturePatternTrussSettings(getState())!,
        [side === 'Left' ? 'leftEndSettings' : 'rightEndSettings']: settings,
      }),
    );
  };

export const setInsertionComponentZoneSettings =
  (zoneIndex: number, updatedZoneSettings: ZoneSettings): ThunkActionVoid =>
  (dispatch, getState) => {
    const oldSettings = getActiveFeaturePatternTrussSettings(getState())!;
    const updatedSettings = produce(oldSettings, (draft) => {
      draft.zoneSettings[zoneIndex] = updatedZoneSettings;
    });

    dispatch(updateActiveFeaturePatternTrussSettings(updatedSettings));
  };

export const updateActiveZoneSettings =
  (settings: ZoneSettings, zoneGuid: string): AsyncThunkActionReturn<boolean> =>
  async (dispatch, getState) => {
    const state = getState();

    const { insertingObjectInfo } = state.application;
    if (!insertingObjectInfo || insertingObjectInfo.type !== 'componentDesign') {
      const activeComponentDesign = getActiveComponentDesign(state);
      if (!activeComponentDesign) {
        throw new Error('No truss is active.');
      }

      return dispatch(editZone(activeComponentDesign, settings, zoneGuid));
    }

    return false;
  };

export const replaceLumbers =
  (
    componentDesignGuids: string[],
    replacementRequest: { filter: LumberFilter; newLumber: Lumber },
  ): AsyncThunkActionReturn<string[]> =>
  async (dispatch, getState) => {
    const spinnerId = dispatch(addSpinnerMessage('Replacing lumbers...'));

    try {
      const successfulComponentNames = await Promise.all(
        componentDesignGuids.map(async (guid) => {
          const componentDesign = getState().componentDesigns.data[guid];
          if (!componentDesign) throw new Error('Could not find component design');
          const replaceLumberResponse = await designService.componentDesigns.postReplaceLumber({
            componentDesign,
            ...replacementRequest,
          });
          // Once lumber replacement is finished, we need to update it.
          if (replaceLumberResponse.didReplace) {
            dispatch(setUnsavedComponentDesignFromServer(replaceLumberResponse.componentDesign));
          }
          return replaceLumberResponse.didReplace ? replaceLumberResponse.componentDesign.component.name : null;
        }),
      );

      return successfulComponentNames.filter(notNullish);
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const editEndCondition =
  (componentDesign: ComponentDesign, endConditionSettings: EndConditionSettings): AsyncThunkActionReturn<boolean> =>
  async (dispatch) => {
    const spinnerId = dispatch(addSpinnerMessage('Editing end condition...'));

    try {
      const updatedComponent = await designService.componentMacros.editEndCondition(
        componentDesign,
        endConditionSettings,
      );

      dispatch(setComponent({ componentDesignGuid: componentDesign.guid, component: updatedComponent }));
    } catch (exception) {
      console.error(exception);
      dispatch(enqueueError(getUserVisibleOrDefaultExceptionMessage(exception, 'Error editing end condition.')));

      return false;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }

    return true;
  };

export const editZone =
  (componentDesign: ComponentDesign, zoneSettings: ZoneSettings, zoneGuid: string): AsyncThunkActionReturn<boolean> =>
  async (dispatch) => {
    if (!isFeaturePatternTruss(componentDesign.component)) {
      throw new Error("Can't edit zones of a profile component.");
    }

    const editedZoneIndex = componentDesign.component.zones.findIndex((s) => s.guid === zoneGuid);
    if (editedZoneIndex == null || editedZoneIndex === -1) {
      throw new Error("Can't find zone to be edited.");
    }

    const spinnerId = dispatch(addSpinnerMessage('Editing zone...'));

    try {
      const zoneSettingsWithGuid = { ...zoneSettings, guid: zoneGuid };
      const updatedComponent = await designService.componentMacros.editZone(componentDesign, zoneSettingsWithGuid);
      dispatch(setComponent({ componentDesignGuid: componentDesign.guid, component: updatedComponent }));
    } catch (exception) {
      console.error(exception);
      dispatch(enqueueError('Error editing zone.'));

      return false;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }

    return true;
  };

export const stub =
  (
    componentSide: ComponentSide,
    stubDistanceOption: StubDistanceOption,
    stubDistance: number,
  ): AsyncThunkActionReturn<ComponentDesign | undefined> =>
  async (dispatch, getState) => {
    const state = getState();

    const componentDesign = getActiveComponentDesign(state);
    if (!componentDesign) return undefined;

    const spinnerId = dispatch(addSpinnerMessage(`Stubbing ${componentDesign.component.name}...`));

    try {
      const updatedComponentDesign = await designService.componentDesigns.postStub(
        componentDesign,
        componentSide,
        stubDistanceOption,
        stubDistance,
      );
      dispatch(setUnsavedComponentDesignFromServer(updatedComponentDesign));

      return updatedComponentDesign;
    } catch {
      dispatch(enqueueError(`Unable to stub ${componentDesign.component.name}.`));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const gable =
  (gableSettings: GableSettings): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    const componentDesign = getActiveComponentDesign(state);
    if (!componentDesign || componentDesign.component.definitionType === 'Wall') return;
    const materialSettings = await designService.settings.getModelMaterialSettings(componentDesign.guid);
    const gableStudLumber = getLumberForMemberTypeOrDefault(
      'GableStud',
      materialSettings,
      componentDesign.component.definitionType,
    );

    const spinnerId = dispatch(addSpinnerMessage(`Gabling ${componentDesign.component.name}...`));

    try {
      const updatedComponentDesign = await designService.componentDesigns.postGable(
        componentDesign,
        gableSettings,
        gableStudLumber,
      );
      dispatch(setUnsavedComponentDesignFromServer(updatedComponentDesign));
    } catch {
      dispatch(enqueueError(`Unable to gable ${componentDesign.component.name}.`));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
